<template>
	<journey-wrapper>
		<stock-summary v-if="store.journey" :journey="store.journey" />

		<journey-notes v-if="store.journey" :journey="store.journey" />

		<contact-information v-if="store.journey && showContactInfo" :journey="store.journey" />

		<!-- Maps -->
		<app-panel v-if="store.journey" class="mt-3">
			<app-tabs :nav="nav" icon-size="small" justify lazy-load>
				<template #tab1>
					<p class="mt-2 mb-4 text-xs">Address: {{ store.journey.delivery_address }}</p>
					<GoogleMap :address="store.journey.delivery_address" />
				</template>

				<template v-if="store.journey.what_3_words" #tab2>
					<p class="mt-2 mb-4 text-xs">Address: {{ store.journey.delivery_address }}</p>

					<p class="mt-2 mb-4 text-xs">what3words: {{ store.journey.what_3_words }}</p>

					<GoogleMap :what3words="store.journey.what_3_words" />
				</template>
			</app-tabs>
		</app-panel>

		<div v-if="store.journey?.site_arrive_at" class="flex gap-3 mt-3">
			<app-badge>
				Arrived: {{ new Date(store.journey?.site_arrive_at).toLocaleTimeString() }}
			</app-badge>

			<app-badge v-if="store.journey?.load_start_at">
				{{ loadUnload }} Start:
				{{ new Date(store.journey?.load_start_at).toLocaleTimeString() }}
			</app-badge>

			<app-badge v-if="store.journey?.site_depart_at">
				{{ loadUnload }} End:
				{{ new Date(store.journey?.site_depart_at).toLocaleTimeString() }}
			</app-badge>
		</div>

		<div v-if="store.showActions">
			<!-- Buttons -->
			<app-button
				v-if="!store.journey!.site_arrive_at"
				class="w-full mt-3"
				size="large"
				@click="openConfirm('Confirm Arrival', store.arrived)">
				Confirm Arrival Time {{ currentTime }}
			</app-button>

			<app-button
				v-if="store.journey?.site_arrive_at && !store.journey?.load_start_at"
				class="w-full mt-3"
				size="large"
				@click="openConfirm(`Confirm ${loadUnload} Start`, store.start_loading)">
				Confirm {{ loadUnload }} Start Time {{ currentTime }}
			</app-button>

			<app-button
				v-if="store.journey?.load_start_at && !store.journey?.site_depart_at"
				class="w-full mt-3"
				size="large"
				@click="openConfirm(`Confirm ${loadUnload} Complete`, store.end_loading)">
				Confirm {{ loadUnload }} Complete Time {{ currentTime }}
			</app-button>

			<Confirm
				:title="confirmTitle"
				:open="confirm"
				theme="warning"
				@canceled="closeConfirm"
				@confirmed="confirmTimestamp">
				{{ currentTime }}
			</Confirm>
		</div>

		<journey-nav
			:current="1"
			prev="/driver-app"
			next="photo"
			:disabled="!store.step1Complete" />
	</journey-wrapper>
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
	import StockSummary from '@/components/partials/driver-app/StockSummary.vue'
	import ContactInformation from '@/components/partials/driver-app/ContactInformation.vue'
	import GoogleMap from '@/components/partials/GoogleMap.vue'
	import JourneyNotes from '@/components/partials/driver-app/JourneyNotes.vue'
	import JourneyNav from '@/components/partials/driver-app/JourneyNav.vue'
	import useDriverApp from '@/stores/driver-app'
	import JourneyWrapper from '@/components/layout/driver-app/JourneyWrapper.vue'
	import Confirm from '@/components/partials/dialogs/Confirm.vue'

	// Resources
	const store = useDriverApp()

	// Data
	const currentTime = ref(new Date().toLocaleTimeString())
	let timer: ReturnType<typeof setInterval> | undefined

	const confirmEvent = ref<Function | null>()
	const confirmTitle = ref('')
	const confirm = ref(false)

	const showContactInfo = computed(() => {
		return store.journey?.site_contact_telephone || store.journey?.customer_contact_telephone
	})

	const loadUnload = computed(() => {
		return store.journey?.type == 'DEL' ? 'Unload' : 'Load'
	})

	const nav = ref([
		{
			id: 'googleMaps',
			name: 'Google Maps',
			icon: ['fas', 'map']
		},

		{
			id: 'what3Words',
			name: 'what3words',
			icon: ['fas', 'map-pin'],
			disabled: true
		}
	])

	function updateTime() {
		currentTime.value = new Date().toLocaleTimeString()
	}

	function startTimer() {
		timer = setInterval(updateTime, 1000)
	}

	function stopTimer() {
		if (timer) {
			clearInterval(timer)
		}
	}

	function openConfirm(title: string, method: Function) {
		confirmEvent.value = method
		confirmTitle.value = title
		confirm.value = true
	}

	function closeConfirm() {
		confirmEvent.value = null
		confirmTitle.value = ''
		confirm.value = false
	}

	function confirmTimestamp() {
		if (confirmEvent.value) {
			confirmEvent.value()
		}

		closeConfirm()
	}

	watch(
		() => store.journey,
		(journey) => {
			if (journey?.what_3_words) {
				nav.value[1].disabled = false
			}
		}
	)

	onMounted(() => {
		startTimer()
	})

	onBeforeUnmount(() => {
		stopTimer()
	})
</script>
